import { useEffect, useState } from "react";
import {
  getAttributionBreakdown,
  getAttributionDrilldown,
} from "services/marketing-analytics.service";
import { fetchUtmLinks } from "services/utm.service";
import { isCustomRangeIncomplete } from "utils/analyticsQueryParams";
import {
  buildAttributionRows,
  buildFilterOptions,
  computeInsights,
  filterAttributionRows,
} from "pages/analytics/traffic-attribution/utils/attributionDashboardUtils";

const EMPTY_FILTERS = { platform: null, account: null, campaign: null };

export default function useAttributionDashboard(api, dateFilters, drilldownFilters = EMPTY_FILTERS) {
  const [attributionDrilldown, setAttributionDrilldown] = useState(null);
  const [attributionBreakdown, setAttributionBreakdown] = useState(null);
  const [utmLinks, setUtmLinks] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isCustomRangeIncomplete(dateFilters)) {
      setIsLoading(false);
      setAttributionDrilldown(null);
      setAttributionBreakdown(null);
      setUtmLinks([]);
      setError(null);
      return;
    }

    let cancelled = false;

    const load = async () => {
      setIsLoading(true);
      setError(null);

      const [drilldownRes, breakdownRes, utmRes] = await Promise.allSettled([
        getAttributionDrilldown(api, { filters: dateFilters }),
        getAttributionBreakdown(api, { filters: dateFilters }),
        fetchUtmLinks(api, {}),
      ]);

      if (cancelled) return;

      const drilldown = drilldownRes.status === "fulfilled" ? drilldownRes.value : null;
      const breakdown = breakdownRes.status === "fulfilled" ? breakdownRes.value : null;
      const utm = utmRes.status === "fulfilled" ? utmRes.value?.records ?? [] : [];

      setAttributionDrilldown(drilldown);
      setAttributionBreakdown(breakdown);
      setUtmLinks(utm);

      const allFailed = [drilldownRes, breakdownRes, utmRes].every((r) => r.status === "rejected");
      setError(allFailed ? "Failed to load attribution data." : null);
      setIsLoading(false);
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [
    api,
    dateFilters.dateRangeType,
    dateFilters.customDateRange?.[0],
    dateFilters.customDateRange?.[1],
  ]);

  const allRows = buildAttributionRows(attributionDrilldown);

  const rows = filterAttributionRows(allRows, drilldownFilters);

  const filterOptions = buildFilterOptions(allRows, utmLinks, attributionBreakdown);

  const insights = computeInsights(allRows, attributionBreakdown);

  return {
    rows,
    allRows,
    filterOptions,
    insights,
    isLoading,
    error,
    isRangeIncomplete: isCustomRangeIncomplete(dateFilters),
  };
}
